import { useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { HpButton } from '@hp/ui';

/**
 * Modal confirmation for consequential decisions (native `<dialog>`, so Escape and the backdrop
 * behave as users expect). The body explains what will happen; the confirm button is the only
 * way to proceed. Rendered always, opened via `open`.
 */
export function ConfirmDialog({
  open,
  title,
  confirmLabel,
  danger = false,
  busy = false,
  onConfirm,
  onCancel,
  children,
}: {
  open: boolean;
  title: string;
  confirmLabel: string;
  danger?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  children: ReactNode;
}) {
  const { t } = useTranslation('common');
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      className="bo-dialog"
      aria-labelledby="bo-dialog-title"
      onCancel={(e) => {
        e.preventDefault();
        onCancel();
      }}
      onClick={(e) => {
        // a click on the backdrop lands on the dialog element itself, not on its content
        if (e.target === ref.current) onCancel();
      }}
    >
      <div className="bo-dialog__panel">
        <h2 id="bo-dialog-title" className="bo-dialog__title">
          {title}
        </h2>
        <div className="bo-dialog__body">{children}</div>
        <div className="bo-dialog__actions">
          <HpButton onClick={onCancel} disabled={busy}>
            {t('actions.cancel')}
          </HpButton>
          <HpButton
            variant={danger ? 'quiet' : 'primary'}
            className={danger ? 'bo-danger' : undefined}
            icon={danger ? 'trash' : 'check'}
            busy={busy}
            onClick={onConfirm}
            autoFocus={false}
          >
            {confirmLabel}
          </HpButton>
        </div>
      </div>
    </dialog>
  );
}
